import { type ReactNode, useEffect, useRef, useState } from 'react';
import type { LucideIcon } from 'lucide-react';
import { Award, Bot, CalendarClock, ChevronDown, GitBranch, Info, LifeBuoy, LineChart, Mail, MessagesSquare, Users } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/hooks/use-auth';
import { cn } from '@/lib/utils';
import { IndustryPreviewVisual, INDUSTRIES } from '@/modules/marketing/components/industry-visuals';
import { PLATFORM_FEATURES, PlatformPreviewVisual } from '@/modules/marketing/components/platform-visuals';
import { PRICINGCRM_FEATURES, PRICINGCRM_INDUSTRIES } from '@/modules/marketing/components/pricingcrm-content';
import { PRODUCTS } from '@/modules/marketing/components/product-visuals';
import { usePlatformBranding } from '@/modules/marketing/hooks/use-platform-branding';

export const FEATURES = [
    {
        slug: 'ai-assistant',
        icon: Bot,
        title: 'AI Assistant',
        description:
            'A guided, question-based chat widget that qualifies visitors and hands off warm leads — embedded on your site with one script tag. Try the live chat bubble in the corner of this page.',
    },
    {
        slug: 'guided-questions',
        icon: GitBranch,
        title: 'Guided Questions',
        description:
            'Build a branching decision tree once — each answer leads to the next question, a knowledge-base lookup, or a warm handoff.',
    },
    {
        slug: 'pricing-intelligence',
        icon: LineChart,
        title: 'Pricing Intelligence',
        description: 'Market-aware pricing recommendations built from your own performance data, competitor sets, and goals.',
    },
    {
        slug: 'crm-leads',
        icon: Users,
        title: 'CRM & Leads',
        description: 'Every conversation becomes a tracked lead, scored and routed to your team automatically.',
    },
    {
        slug: 'appointments',
        icon: CalendarClock,
        title: 'Appointments',
        description: 'Let visitors request a time directly from a conversation — synced straight to your team’s calendar.',
    },
    {
        slug: 'multi-channel',
        icon: MessagesSquare,
        title: 'Multi-channel',
        description: 'The same assistant reaches visitors over web chat, voice, and WhatsApp from one shared configuration.',
    },
];

const INDUSTRIES_MENU: MegaMenuItem[] = INDUSTRIES.map((i) => ({
    icon: i.icon,
    title: i.name,
    description: i.tagline,
    href: `/industries/${i.slug}`,
}));

const PRICINGCRM_INDUSTRIES_MENU: MegaMenuItem[] = PRICINGCRM_INDUSTRIES.map((i) => ({
    icon: i.icon,
    title: i.name,
    description: i.tagline,
    href: `/industries/${i.slug}`,
}));

// PricingCRM's 8 real features split across the two menus AIPA's header already has:
// the first 4 (customer/pricing-facing) route through /products/:slug, the rest through
// /platform/:slug — see ProductDetailPage/PlatformDetailPage's PricingCRM fallback lookup.
const PRICINGCRM_PRODUCTS_MENU: MegaMenuItem[] = PRICINGCRM_FEATURES.slice(0, 4).map((f) => ({
    icon: f.icon,
    title: f.title,
    description: f.description,
    href: `/products/${f.slug}`,
}));

const PRICINGCRM_PLATFORM_MENU: MegaMenuItem[] = PRICINGCRM_FEATURES.slice(4).map((f) => ({
    icon: f.icon,
    title: f.title,
    description: f.description,
    href: `/platform/${f.slug}`,
}));

const PRODUCTS_MENU: MegaMenuItem[] = PRODUCTS.map((p) => ({
    icon: p.icon,
    title: p.title,
    description: p.description,
    href: `/products/${p.key}`,
}));

const PLATFORM_MENU_ITEMS: MegaMenuItem[] = PLATFORM_FEATURES.map((p) => ({
    icon: p.icon,
    title: p.title,
    description: p.tagline,
    href: `/platform/${p.slug}`,
}));

const RESOURCES_MENU = [
    { icon: LifeBuoy, label: 'Help Center', description: 'Answers to common setup and usage questions', href: '/resources/help' },
    { icon: Award, label: 'Why AIPA', description: 'How we think about the platform, and who it’s for', href: '/why-aipa' },
];

const PRICINGCRM_RESOURCES_MENU = [
    { icon: LifeBuoy, label: 'Help Center', description: 'Answers to common setup and usage questions', href: '/resources/help' },
    { icon: Award, label: 'Why PricingCRM', description: 'How we think about pricing, and who it’s for', href: '/why-aipa' },
];

const COMPANY_MENU = [
    { icon: Info, label: 'About', description: 'What we’re building and why', href: '/company/about' },
    { icon: Mail, label: 'Contact', description: 'Get in touch with our team', href: '/company/contact' },
];

/**
 * This same marketing site is deployed twice: on aipa.pricingcrm.com (the app's own domain,
 * branding driven by usePlatformBranding() as normal) and, as a static copy of the homepage/nav,
 * on the company's root domain pricingcrm.com — where the app itself isn't installed. On that
 * root domain the logo is hardcoded to the company's own "PricingCRM" mark instead of whatever
 * the platform-wide branding API returns, and "Log in" must leave the SPA entirely and go to the
 * real app on aipa.pricingcrm.com rather than an internal (nonexistent, on this domain) /login route.
 */
export const AIPA_APP_ORIGIN = 'https://aipa.pricingcrm.com';
const MARKETING_ONLY_HOSTNAMES = ['pricingcrm.com', 'www.pricingcrm.com'];

export function isMarketingOnlyDomain(): boolean {
    return typeof window !== 'undefined' && MARKETING_ONLY_HOSTNAMES.includes(window.location.hostname);
}

/**
 * `heightClassName` must be a definite (non-percentage) height — the img's
 * own `h-full` only resolves correctly if ITS immediate parent (this Link)
 * has one. The header passes `h-full` because its own row already has a
 * fixed `h-16`, so that resolves fine one level further up; anywhere else
 * (footer, sidebar) the ancestor chain's height is content-driven, and
 * `h-full` on both the Link and the img at once collapses to a 0-width
 * image — hence a caller-supplied fixed height rather than a shared default.
 */
export function Logo({ heightClassName = 'h-full' }: { heightClassName?: string } = {}) {
    const { data: branding, isLoading } = usePlatformBranding();
    const name = branding?.platform_name || 'AIPA';

    if (isMarketingOnlyDomain()) {
        return (
            <Link to="/" className={cn('flex items-center gap-2', heightClassName)}>
                <img src="/pricingcrm-logo.png" alt="PricingCRM" className="h-full w-auto max-w-[40px] object-contain" />
                <span className="text-lg font-semibold">PricingCRM</span>
            </Link>
        );
    }

    // Renders nothing at all while the branding fetch is in flight — better a
    // brief blank space than a flash of the plain-text fallback (or, on a
    // second render, the previous logo) before the real configured logo swaps
    // in a moment later.
    if (isLoading) {
        return <Link to="/" className={cn('flex items-center', heightClassName)} aria-label={name} />;
    }

    return (
        <Link to="/" className={cn('flex items-center gap-2', heightClassName)}>
            {branding?.logo_url ? (
                <img src={branding.logo_url} alt={name} className="h-full w-auto max-w-[260px] object-contain" />
            ) : (
                <>
                    <div className="flex h-9 w-9 items-center justify-center rounded-md bg-primary text-base font-bold text-primary-foreground">
                        {name.charAt(0).toUpperCase()}
                    </div>
                    <span className="text-lg font-semibold">{name}</span>
                </>
            )}
        </Link>
    );
}

type NavMenuItem = { icon: LucideIcon; label: string; description?: string; href: string };

/**
 * Hover-opens (with a short close delay so moving the mouse into the panel doesn't dismiss it); click also opens (idempotent,
 * for touch/keyboard use) and closes via an outside click or Escape — never via the triggering click itself, since a real
 * mouse click always fires `mouseenter` first, which would otherwise open-then-immediately-close on every click.
 */
function NavMenu({ label, items }: { label: string; items: NavMenuItem[] }) {
    const [open, setOpen] = useState(false);
    const closeTimer = useRef<ReturnType<typeof setTimeout>>();
    const rootRef = useRef<HTMLDivElement>(null);

    const show = () => {
        window.clearTimeout(closeTimer.current);
        setOpen(true);
    };
    const scheduleHide = () => {
        closeTimer.current = setTimeout(() => setOpen(false), 150);
    };

    useEffect(() => () => window.clearTimeout(closeTimer.current), []);

    useEffect(() => {
        if (!open) return undefined;

        function handlePointerDown(e: PointerEvent) {
            if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
        }
        function handleKeyDown(e: KeyboardEvent) {
            if (e.key === 'Escape') setOpen(false);
        }

        document.addEventListener('pointerdown', handlePointerDown);
        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('pointerdown', handlePointerDown);
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [open]);

    return (
        <div ref={rootRef} className="relative" onMouseEnter={show} onMouseLeave={scheduleHide}>
            <button type="button" className="flex items-center gap-1 py-2 hover:text-foreground" aria-expanded={open} onClick={show}>
                {label}
                <ChevronDown className={`size-3.5 transition-transform ${open ? 'rotate-180' : ''}`} />
            </button>
            {open && (
                <div className="absolute left-1/2 top-full z-20 w-64 -translate-x-1/2 pt-3">
                    <div className="grid gap-0.5 rounded-lg border bg-popover p-1.5 text-popover-foreground shadow-lg">
                        {items.map((item) => (
                            <a
                                key={item.label}
                                href={item.href}
                                onClick={() => setOpen(false)}
                                className="flex items-center gap-2.5 rounded-md px-2.5 py-2 text-left transition-colors hover:bg-accent"
                            >
                                <item.icon className="size-3.5 shrink-0 text-primary" />
                                <span className="text-sm font-medium text-foreground">{item.label}</span>
                            </a>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}

interface MegaMenuItem {
    icon: LucideIcon;
    title: string;
    description: string;
    href: string;
}

/**
 * A rich dropdown — a scrollable list of items on the left (icon, title, one-line description), and a large preview
 * panel on the right that swaps to whichever item is hovered: a big image, its full description, and a "Learn more"
 * link. Same hover/click/outside-click/Escape interaction contract as NavMenu (see its docblock for why the click
 * handler must not toggle), just wider and with a live preview pane instead of a plain list.
 */
function MegaMenu({
    label,
    items,
    renderVisual,
}: {
    label: string;
    items: MegaMenuItem[];
    renderVisual: (item: MegaMenuItem) => ReactNode;
}) {
    const [open, setOpen] = useState(false);
    const [active, setActive] = useState(0);
    const closeTimer = useRef<ReturnType<typeof setTimeout>>();
    const rootRef = useRef<HTMLDivElement>(null);

    const show = () => {
        window.clearTimeout(closeTimer.current);
        setOpen(true);
    };
    const scheduleHide = () => {
        closeTimer.current = setTimeout(() => setOpen(false), 150);
    };

    useEffect(() => () => window.clearTimeout(closeTimer.current), []);

    useEffect(() => {
        if (!open) return undefined;

        function handlePointerDown(e: PointerEvent) {
            if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
        }
        function handleKeyDown(e: KeyboardEvent) {
            if (e.key === 'Escape') setOpen(false);
        }

        document.addEventListener('pointerdown', handlePointerDown);
        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('pointerdown', handlePointerDown);
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [open]);

    const activeItem = items[active] ?? items[0];

    return (
        <div ref={rootRef} className="relative" onMouseEnter={show} onMouseLeave={scheduleHide}>
            <button type="button" className="flex items-center gap-1 py-2 hover:text-foreground" aria-expanded={open} onClick={show}>
                {label}
                <ChevronDown className={`size-3.5 transition-transform ${open ? 'rotate-180' : ''}`} />
            </button>
            {open && (
                <div className="absolute left-1/2 top-full z-20 w-[640px] -translate-x-1/2 pt-3">
                    <div className="grid grid-cols-[260px_1fr] overflow-hidden rounded-2xl border bg-popover text-popover-foreground shadow-2xl">
                        <div className="max-h-[420px] space-y-0.5 overflow-y-auto p-2">
                            {items.map((item, i) => (
                                <a
                                    key={item.title}
                                    href={item.href}
                                    onMouseEnter={() => setActive(i)}
                                    onClick={() => setOpen(false)}
                                    className={cn(
                                        'flex items-start gap-2.5 rounded-lg px-3 py-2 text-left transition-colors',
                                        i === active ? 'bg-accent' : 'hover:bg-accent/60',
                                    )}
                                >
                                    <item.icon className="mt-0.5 size-4 shrink-0 text-primary" />
                                    <span>
                                        <span className="block text-sm font-semibold text-foreground">{item.title}</span>
                                        <span className="mt-0.5 line-clamp-1 block text-xs text-muted-foreground">{item.description}</span>
                                    </span>
                                </a>
                            ))}
                        </div>
                        <div className="border-l bg-muted/20 p-5">
                            {renderVisual(activeItem)}
                            <h4 className="mt-4 text-base font-bold text-foreground">{activeItem.title}</h4>
                            <p className="mt-1.5 text-sm text-muted-foreground">{activeItem.description}</p>
                            <a
                                href={activeItem.href}
                                onClick={() => setOpen(false)}
                                className="mt-3 inline-flex items-center gap-1 text-sm font-semibold text-primary hover:underline"
                            >
                                Learn more →
                            </a>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

/**
 * Two-tier header used across every marketing page: a slim utility bar
 * (Resources / Company / account link) above the main nav (logo, product
 * dropdowns, primary CTA) — the same general shape as most SaaS marketing
 * sites in this space, built from AIPA's own pages and copy throughout.
 */
export function MarketingHeader() {
    const { user, isAuthenticated, isLoading } = useAuth();
    const dashboardHref = user?.is_platform_admin ? '/admin' : '/dashboard';
    const { data: branding } = usePlatformBranding();

    // Set at runtime, not build time — index.html ships with no <link rel="icon">
    // at all (see its own comment), so a Super Admin-uploaded favicon can take
    // effect without a new frontend build/deploy. On the marketing-only root
    // domain, PricingCRM's own icon always wins over the platform-wide one.
    useEffect(() => {
        const faviconUrl = isMarketingOnlyDomain() ? '/pricingcrm-logo.png' : branding?.favicon_url;
        if (!faviconUrl) return;

        let link = document.querySelector<HTMLLinkElement>("link[rel~='icon']");
        if (!link) {
            link = document.createElement('link');
            link.rel = 'icon';
            document.head.appendChild(link);
        }
        link.href = faviconUrl;
    }, [branding?.favicon_url]);

    // PricingCRM's own crimson brand color, in place of AIPA's blue — see the
    // :root[data-brand="pricingcrm"] token override in app.css. Scoped to this
    // one attribute so nothing else about the design system has to change.
    useEffect(() => {
        if (isMarketingOnlyDomain()) {
            document.documentElement.dataset.brand = 'pricingcrm';
        }
    }, []);

    // Per-page title/meta/OG tags are set by each page itself via
    // usePageSeo() — a single shared title/description for the whole site
    // (all this effect used to do) is worse for search ranking than a unique
    // one per page. This header only ever owns the favicon, which really is
    // global.

    return (
        <header className="sticky top-0 z-10 border-b bg-background/80 backdrop-blur">
            <div className="border-b bg-muted/30">
                <div className="mx-auto flex max-w-6xl items-center justify-end gap-6 px-6 py-1.5 text-xs font-medium text-muted-foreground">
                    <NavMenu label="Resources" items={isMarketingOnlyDomain() ? PRICINGCRM_RESOURCES_MENU : RESOURCES_MENU} />
                    <NavMenu label="Company" items={COMPANY_MENU} />
                    {!isLoading && isAuthenticated ? (
                        <Link to={dashboardHref} className="py-1 hover:text-foreground">
                            Dashboard
                        </Link>
                    ) : isMarketingOnlyDomain() ? (
                        <a href={`${AIPA_APP_ORIGIN}/login`} className="py-1 hover:text-foreground">
                            Log in
                        </a>
                    ) : (
                        <Link to="/login" className="py-1 hover:text-foreground">
                            Log in
                        </Link>
                    )}
                </div>
            </div>
            <div className="mx-auto flex h-16 max-w-6xl items-center justify-between px-6 py-[10px]">
                <Logo />
                <nav className="hidden items-center gap-6 text-sm font-medium text-muted-foreground md:flex">
                    <MegaMenu
                        label="Industries"
                        items={isMarketingOnlyDomain() ? PRICINGCRM_INDUSTRIES_MENU : INDUSTRIES_MENU}
                        renderVisual={(item) => <IndustryPreviewVisual icon={item.icon} />}
                    />
                    <MegaMenu
                        label="Product"
                        items={isMarketingOnlyDomain() ? PRICINGCRM_PRODUCTS_MENU : PRODUCTS_MENU}
                        renderVisual={(item) =>
                            PRODUCTS.find((p) => p.title === item.title)?.visual ?? <IndustryPreviewVisual icon={item.icon} />
                        }
                    />
                    <MegaMenu
                        label="Data & Analysis"
                        items={isMarketingOnlyDomain() ? PRICINGCRM_PLATFORM_MENU : PLATFORM_MENU_ITEMS}
                        renderVisual={(item) => <PlatformPreviewVisual icon={item.icon} />}
                    />
                    <a href="/#how-it-works" className="py-2 hover:text-foreground">
                        How it works
                    </a>
                </nav>
                {!isLoading && isAuthenticated ? (
                    <Button asChild>
                        <Link to={dashboardHref}>Dashboard</Link>
                    </Button>
                ) : (
                    <Button asChild>
                        <Link to="/company/book-a-demo">Book a demo</Link>
                    </Button>
                )}
            </div>
        </header>
    );
}

export function MarketingFooter() {
    const { data: branding } = usePlatformBranding();

    return (
        <footer className="border-t">
            <div className="mx-auto max-w-6xl px-6 py-12">
                <div className="grid grid-cols-2 gap-x-8 gap-y-10 sm:grid-cols-3 lg:grid-cols-6">
                    <div className="col-span-2 sm:col-span-3 lg:col-span-2">
                        <Logo heightClassName="h-9" />
                        <p className="mt-3 max-w-xs text-sm text-muted-foreground">
                            {isMarketingOnlyDomain()
                                ? 'One platform for lead generation, pricing intelligence, and CMA reporting — built for teams that can’t afford to misprice a property.'
                                : 'One platform for conversational lead capture, pricing intelligence, and reputation monitoring — built for teams that can’t afford to miss a lead.'}
                        </p>
                        <div className="mt-4 flex items-center gap-2 text-xs text-muted-foreground">
                            <a href={isMarketingOnlyDomain() ? 'mailto:hello@pricingcrm.com' : 'mailto:hello@aipa.io'} className="hover:text-foreground">
                                {isMarketingOnlyDomain() ? 'hello@pricingcrm.com' : 'hello@aipa.io'}
                            </a>
                        </div>
                    </div>
                    <FooterColumn title="Product">
                        <FooterLink href="/#products">Features</FooterLink>
                        <FooterLink href="/#how-it-works">How it works</FooterLink>
                        <FooterLink to="/why-aipa">{isMarketingOnlyDomain() ? 'Why PricingCRM' : 'Why AIPA'}</FooterLink>
                        <FooterLink to="/company/book-a-demo">Book a demo</FooterLink>
                    </FooterColumn>
                    <FooterColumn title="Industries">
                        {(isMarketingOnlyDomain() ? PRICINGCRM_FOOTER_INDUSTRIES : FOOTER_INDUSTRIES).map((industry) => (
                            <FooterLink key={industry.slug} to={`/industries/${industry.slug}`}>
                                {industry.name}
                            </FooterLink>
                        ))}
                        <FooterLink href="/#industries">View all industries</FooterLink>
                    </FooterColumn>
                    <FooterColumn title="Company">
                        <FooterLink to="/company/about">About</FooterLink>
                        <FooterLink to="/company/contact">Contact</FooterLink>
                        <FooterLink to="/resources/help">Help Center</FooterLink>
                        {isMarketingOnlyDomain() ? (
                            <FooterLink href={`${AIPA_APP_ORIGIN}/login`}>Log in</FooterLink>
                        ) : (
                            <FooterLink to="/login">Log in</FooterLink>
                        )}
                    </FooterColumn>
                    <FooterColumn title="Legal">
                        <FooterLink to="/legal/terms">Terms &amp; Conditions</FooterLink>
                        <FooterLink to="/legal/privacy">Privacy Policy</FooterLink>
                    </FooterColumn>
                </div>
                <div className="mt-10 flex flex-col items-center justify-between gap-3 border-t pt-6 text-xs text-muted-foreground sm:flex-row">
                    <p>
                        &copy; {new Date().getFullYear()} {isMarketingOnlyDomain() ? 'PricingCRM' : branding?.platform_name || 'AIPA'}. All
                        rights reserved.
                    </p>
                    <div className="flex items-center gap-4">
                        <Link to="/legal/terms" className="hover:text-foreground">
                            Terms
                        </Link>
                        <Link to="/legal/privacy" className="hover:text-foreground">
                            Privacy
                        </Link>
                    </div>
                </div>
            </div>
        </footer>
    );
}

const FOOTER_INDUSTRIES = INDUSTRIES.slice(0, 5);
const PRICINGCRM_FOOTER_INDUSTRIES = PRICINGCRM_INDUSTRIES.slice(0, 5);

function FooterColumn({ title, children }: { title: string; children: ReactNode }) {
    return (
        <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{title}</p>
            <ul className="mt-3 space-y-2 text-sm text-muted-foreground">{children}</ul>
        </div>
    );
}

function FooterLink({ to, href, children }: { to?: string; href?: string; children: ReactNode }) {
    return (
        <li>
            {to ? (
                <Link to={to} className="hover:text-foreground">
                    {children}
                </Link>
            ) : (
                <a href={href} className="hover:text-foreground">
                    {children}
                </a>
            )}
        </li>
    );
}
